import { parseApiDate } from "@/components/missions/MissionRow";
import type {
  Artifact,
  FaClientCrmSignals,
  FaClientMember,
} from "@/services/api/agentic-v2";

/** Display label for a household member. */
export function memberDisplayName(member: FaClientMember): string {
  const name = member.name ?? {};
  const preferred = name.preferred?.trim();
  const first = name.first?.trim();
  const last = name.last?.trim();
  if (preferred && last) return `${preferred} ${last}`;
  if (preferred) return preferred;
  const full = [first, last].filter(Boolean).join(" ");
  return full || "Member";
}

/** Compact "Alice · Bob" line for list rows and headers. */
export function memberNamesLine(
  members: FaClientMember[],
  max = 3,
): string | null {
  if (members.length === 0) return null;
  const names = members.slice(0, max).map(memberDisplayName);
  if (members.length > max) {
    names.push(`+${members.length - max}`);
  }
  return names.join(" · ");
}

export function isHouseholdClient(
  type: string | undefined,
  memberCount: number | undefined,
): boolean {
  return type === "household" || (memberCount ?? 0) > 1;
}

/** UK-style date for CRM custom fields and session timestamps. */
export function formatClientDate(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "string") {
    const parsed = parseApiDate(value);
    if (parsed) {
      return parsed.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: value.includes("T") ? "2-digit" : undefined,
        minute: value.includes("T") ? "2-digit" : undefined,
      });
    }
    return value;
  }
  return String(value);
}

export interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
  detail: string;
}

/** Octopus onboarding milestones from CRM signals + fact-find status. */
export function buildOnboardingSteps(
  signals: FaClientCrmSignals | undefined,
  factFindComplete: boolean,
): OnboardingStep[] {
  if (!signals) return [];
  return [
    {
      key: "intro",
      label: "Intro session",
      done: hasValue(signals.intro_session_date),
      detail: formatClientDate(signals.intro_session_date),
    },
    {
      key: "intro_outcome",
      label: "Intro outcome",
      done: signals.intro_outcome_submitted,
      detail: signals.intro_outcome_submitted ? "Submitted" : "Pending",
    },
    {
      key: "starter",
      label: "Starter session",
      done: hasValue(signals.starter_session_date),
      detail: formatClientDate(signals.starter_session_date),
    },
    {
      key: "fact_find",
      label: "Fact-find",
      done: signals.fact_find_complete || factFindComplete,
      detail:
        signals.fact_find_complete || factFindComplete ? "Complete" : "In progress",
    },
  ];
}

function hasValue(value: unknown): boolean {
  return value != null && value !== "";
}

export interface ClientPlanRow {
  plan_id?: string;
  /** Plannr plan display name (e.g. JISA product title). */
  name?: string | null;
  provider?: string;
  type?: string;
  value?: number | Record<string, unknown>;
  policy_number?: string;
  source_crm?: string;
  ownership?: string;
  owner_member_ids?: string[];
  /** From Plannr plan custom field LOA Required. */
  loa_required?: boolean | null;
  loa_sent_to_provider_date?: string | null;
}

export type PlanCategory = "pension" | "investment" | "other";

const PENSION_TYPE_KEYWORDS = [
  "pension",
  "sipp",
  "drawdown",
  "defined benefit",
  "workplace",
  "annuity",
  "avc",
];

const INVESTMENT_TYPE_KEYWORDS = [
  "investment",
  "isa",
  "gia",
  "bond",
  "stocks",
  "shares",
  "unit trust",
  "oeic",
  "portfolio",
  "wrapper",
];

/** Plannr / CRM plan type string → Plans tab section. */
export function classifyPlanCategory(plan: ClientPlanRow): PlanCategory {
  const raw = renderPlanField(plan.type) ?? "";
  const t = raw.toLowerCase().replace(/_/g, " ");
  if (!t) return "other";
  if (t === "db" || PENSION_TYPE_KEYWORDS.some((k) => t.includes(k))) {
    return "pension";
  }
  if (INVESTMENT_TYPE_KEYWORDS.some((k) => t.includes(k))) {
    return "investment";
  }
  return "other";
}

export function groupPlansByCategory(
  plans: ClientPlanRow[],
): Record<PlanCategory, ClientPlanRow[]> {
  const grouped: Record<PlanCategory, ClientPlanRow[]> = {
    pension: [],
    investment: [],
    other: [],
  };
  for (const plan of plans) {
    grouped[classifyPlanCategory(plan)].push(plan);
  }
  return grouped;
}

/**
 * Plannr sometimes nests provider/type/policy/value as objects. Render the
 * best human-readable string and never let a stray dict reach React.
 */
export function renderPlanField(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    for (const key of ["name", "display", "label", "title", "slug"]) {
      const candidate = obj[key];
      if (typeof candidate === "string" && candidate) return candidate;
    }
    return null;
  }
  return String(value);
}

/** Format plan value — Plannr ``{ formatted }`` or ``{ amount: pence }`` objects. */
export function formatPlanValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const formatted = obj.formatted;
    if (typeof formatted === "string" && formatted.trim()) {
      return formatted.trim();
    }
    const pence = parsePlannrPence(obj.amount ?? obj.value);
    if (pence != null) return formatGbp(pence / 100);
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    return formatGbp(value);
  }
  return null;
}

/** Numeric plan value in pounds for aggregation (ignores pre-formatted strings). */
export function parsePlanValueAmount(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const pence = parsePlannrPence(obj.amount ?? obj.value);
    if (pence != null) return pence / 100;
  }
  return null;
}

export function sumPlanValues(plans: ClientPlanRow[]): string | null {
  let total = 0;
  let any = false;
  for (const plan of plans) {
    const amount = parsePlanValueAmount(plan.value);
    if (amount != null) {
      total += amount;
      any = true;
    }
  }
  return any ? formatGbp(total) : null;
}

function parsePlannrPence(amount: unknown): number | null {
  if (typeof amount === "number" && !Number.isNaN(amount)) return amount;
  if (typeof amount === "string" && amount.trim()) {
    const n = Number(amount);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function formatGbp(amount: number): string {
  return `£${amount.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

export type LoaTriState = "yes" | "no" | "na" | "unknown";

export function planLoaRequiredState(plan: ClientPlanRow): LoaTriState {
  if (plan.loa_required === true) return "yes";
  if (plan.loa_required === false) return "no";
  return "unknown";
}

export function planLoaSentState(plan: ClientPlanRow): LoaTriState {
  if (plan.loa_required === false) return "na";
  if (plan.loa_sent_to_provider_date) return "yes";
  if (plan.loa_required === true) return "no";
  return "unknown";
}

export function showPlanLoaStatus(category: PlanCategory): boolean {
  return category === "pension" || category === "investment";
}

/** Short UK date for plan-row artefact labels (e.g. cheat sheet generated on). */
export function formatPlanArtifactDate(
  iso: string | null | undefined,
): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export interface PlanArtifactLinks {
  loa?: Artifact;
  cheatSheet?: Artifact;
}

const CHEAT_SHEET_SERIES_PREFIX = "cheat_sheet:";
const LOA_SERIES_PREFIX = "loa:";

/** Plan ids referenced by a cheat-sheet artefact (metadata + ``series_key``). */
export function planKeysForCheatSheetArtifact(artifact: Artifact): string[] {
  const keys = new Set<string>();
  const meta = artifact.metadata as
    | { plan_id?: string; plan_uuid?: string }
    | undefined;
  if (meta?.plan_uuid) keys.add(meta.plan_uuid);
  if (meta?.plan_id) keys.add(meta.plan_id);
  const series = artifact.series_key;
  if (
    typeof series === "string" &&
    series.startsWith(CHEAT_SHEET_SERIES_PREFIX)
  ) {
    const planId = series.slice(CHEAT_SHEET_SERIES_PREFIX.length).trim();
    if (planId) keys.add(planId);
  }
  return [...keys];
}

function planKeysForLoaArtifact(artifact: Artifact): string[] {
  const keys = new Set<string>();
  const meta = artifact.metadata as
    | { plan_id?: string; plan_uuid?: string }
    | undefined;
  if (meta?.plan_uuid) keys.add(meta.plan_uuid);
  if (meta?.plan_id) keys.add(meta.plan_id);
  const series = artifact.series_key;
  if (typeof series === "string" && series.startsWith(LOA_SERIES_PREFIX)) {
    const planId = series.slice(LOA_SERIES_PREFIX.length).trim();
    if (planId) keys.add(planId);
  }
  return [...keys];
}

function artifactRecencyMs(artifact: Artifact): number {
  const created = Date.parse(artifact.created_at ?? "");
  if (Number.isFinite(created)) return created;
  const updated = Date.parse(artifact.updated_at ?? "");
  if (Number.isFinite(updated)) return updated;
  return 0;
}

/** Prefer newest row by time; active beats archived at the same timestamp. */
function isNewerPlanArtifact(candidate: Artifact, current: Artifact): boolean {
  const candidateMs = artifactRecencyMs(candidate);
  const currentMs = artifactRecencyMs(current);
  if (candidateMs !== currentMs) return candidateMs > currentMs;
  if (candidate.status === "active" && current.status !== "active") return true;
  if (candidate.status !== "active" && current.status === "active") return false;
  if (candidate.version !== current.version) {
    return candidate.version > current.version;
  }
  return candidate._id > current._id;
}

/** Latest LOA + cheat sheet per Plannr plan uuid. */
export function buildPlanArtifactIndex(
  artifacts: Artifact[],
): Map<string, PlanArtifactLinks> {
  const index = new Map<string, PlanArtifactLinks>();

  const consider = (
    planId: string,
    field: keyof PlanArtifactLinks,
    artifact: Artifact,
  ) => {
    const entry = index.get(planId) ?? {};
    const current = entry[field];
    if (!current || isNewerPlanArtifact(artifact, current)) {
      entry[field] = artifact;
      index.set(planId, entry);
    }
  };

  for (const artifact of artifacts) {
    if (isLoaArtifact(artifact)) {
      if (artifact.status !== "active") continue;
      for (const planId of planKeysForLoaArtifact(artifact)) {
        consider(planId, "loa", artifact);
      }
      continue;
    }
    if (artifact.kind !== "plan_cheat_sheet") continue;
    for (const planId of planKeysForCheatSheetArtifact(artifact)) {
      consider(planId, "cheatSheet", artifact);
    }
  }
  return index;
}

/** Resolve the cheat sheet to open for a plan row (DG id wins over index). */
export function resolvePlanCheatSheetArtifact(
  planId: string | undefined,
  links: PlanArtifactLinks | undefined,
  cheatSheetArtifactId: string | null | undefined,
  artifactsById: ReadonlyMap<string, Artifact>,
): Artifact | undefined {
  if (cheatSheetArtifactId) {
    const fromDg = artifactsById.get(cheatSheetArtifactId);
    if (fromDg) return fromDg;
  }
  return links?.cheatSheet;
}

export function buildArtifactById(
  artifacts: Iterable<Artifact>,
): Map<string, Artifact> {
  const map = new Map<string, Artifact>();
  for (const artifact of artifacts) {
    map.set(artifact._id, artifact);
  }
  return map;
}

export function isLoaArtifact(artifact: Artifact): boolean {
  if (artifact.kind === "loa") return true;
  return (
    artifact.kind === "react_form" &&
    (artifact.metadata as { business_kind?: string } | undefined)
      ?.business_kind === "loa"
  );
}

/** LOA artefacts backed by editable ``form_data`` (agentic v2). */
export function isReactFormLoaArtifact(artifact: Artifact): boolean {
  return isLoaArtifact(artifact) && artifact.kind === "react_form";
}

/** Resolve plan owners to member names for the Plans tab. */
export function planOwnersLabel(
  plan: ClientPlanRow,
  members: FaClientMember[],
): string | null {
  const ids = plan.owner_member_ids ?? [];
  if (ids.length === 0) {
    if (plan.ownership === "joint") return "Joint";
    return null;
  }
  const byId = new Map(members.map((m) => [m.id, memberDisplayName(m)]));
  const names = ids.map((id) => byId.get(id)).filter(Boolean) as string[];
  if (names.length === 0) {
    return plan.ownership === "joint" ? "Joint" : null;
  }
  if (names.length > 1 || plan.ownership === "joint") {
    return names.join(" & ");
  }
  return names[0] ?? null;
}
